import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { LuLoader2, LuChevronDown, LuCheck } from 'react-icons/lu';
import { planosAPI, formatters } from '../services/api';

const SpinningLoader = styled(LuLoader2)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Loader = () => <SpinningLoader size={24} />;

const StateBox = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 48px 24px;
  color: ${props => props.$error ? props.theme.colors.error : props.theme.colors.textSecondary};
`;

const StateTitle = styled.div`
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ReloadButton = styled.button`
  margin-top: 16px;
  padding: 10px 20px;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border-radius: 8px;
  font-weight: 600;

  &:hover { background: ${props => props.theme.colors.hoverPrimary}; }
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const PlanCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1.5px solid ${props => props.selected ? props.theme.colors.secondary : props.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
  box-shadow: ${props => props.selected ? props.theme.shadows.md : props.theme.shadows.sm};

  &:hover {
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: ${props => props.theme.shadows.md};
  }

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

// Badge único (acento de marca). Sem cores ad-hoc por tipo de plano.
const PlanBadge = styled.div`
  position: absolute;
  top: -10px;
  right: 20px;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const PlanHeader = styled.div`
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const RadioInput = styled.input`
  position: absolute;
  width: 22px;
  height: 22px;
  margin: 0;
  opacity: 0;
  cursor: pointer;
`;

const SelectionCircle = styled.div`
  width: 22px;
  height: 22px;
  margin-top: 3px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? props.theme.colors.secondary : '#cbd5e1'};
  background: ${props => props.$selected ? props.theme.colors.secondary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  svg {
    color: ${props => props.theme.colors.white};
    opacity: ${props => props.$selected ? 1 : 0};
    transform: scale(${props => props.$selected ? 1 : 0.4});
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
`;

const PlanInfo = styled.div`
  flex: 1;
`;

const PlanTitle = styled.label`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  display: block;
  margin-bottom: 5px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const PlanPriceRange = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.successGreen};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const PlanDescription = styled.div`
  color: ${props => props.theme.colors.grayMedium};
  margin-bottom: 15px;
  font-size: 13px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 10px;
  }
`;

const PlanDetailsToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 0;
  margin-top: 5px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.secondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;

const ExpandIcon = styled(LuChevronDown)`
  transition: transform 0.2s ease;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CollapsibleDetails = styled.div`
  max-height: ${props => props.$expanded ? '800px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 3px 0;
    color: ${props => props.theme.colors.grayDark};
    font-size: 13px;

    svg {
      flex-shrink: 0;
      margin-top: 2px;
      color: ${props => props.theme.colors.successGreen};
    }
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  margin-top: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;


const PlanSelector = ({ onPlanSelect, selectedPlan, error }) => {
  const [pricing, setPricing] = useState(null);
  const [basePlans, setBasePlans] = useState(null); // Inicia null para mostrar loading
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [expandedPlans, setExpandedPlans] = useState(new Set());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && selectedPlan?.idPlano) {
      setExpandedPlans((prev) => new Set([...prev, selectedPlan.idPlano]));
    }
  }, [isMobile, selectedPlan?.idPlano]);

  const togglePlanDetails = (idPlano, e) => {
    e.stopPropagation();
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(idPlano)) {
        next.delete(idPlano);
      } else {
        next.add(idPlano);
      }
      return next;
    });
  };

  // Carregar planos e pricing juntos
  useEffect(() => {
    let mounted = true;
    
    const loadPlansData = async () => {
      setLoading(true);
      setLoadError(null);
      
      try {
        // Carregar planos e pricing em paralelo
        const [planosRes, pricingRes] = await Promise.all([
          planosAPI.getPlanos(),
          planosAPI.getValores()
        ]);
        
        if (!mounted) return;
        
        // Processar planos (agora organizados por idPlano)
        if (planosRes.data?.success && planosRes.data?.data) {
          setBasePlans(planosRes.data.data);
        } else {
          setLoadError('Não foi possível carregar os planos.');
        }

        // Processar pricing (estrutura simplificada: { planos: {}, dependentes: {} })
        const pricingData = pricingRes.data?.data?.pricing || null;
        setPricing(pricingData);

      } catch (err) {
        console.error('Erro ao carregar dados dos planos:', err);
        if (mounted) {
          setLoadError('Não foi possível carregar os planos. Tente novamente.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    loadPlansData();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Constrói o objeto do plano com precificação dinâmica usando idPlano
  // PETs são tratados como planos normais
  const buildPlan = useCallback((basePlan) => {
    // Se pricing não foi carregado, retornar com valores temporários
    if (!pricing || !pricing.planos) {
      return {
        ...basePlan,
        priceRange: 'Carregando valores...',
        pricingTiers: [],
        minValue: null,
      };
    }

    const idPlano = String(basePlan.idPlano);
    const planData = pricing.planos[idPlano];
    
    let priceRange = 'Valores indisponíveis';
    let pricingTiers = [];
    let minValue = null;

    // Todos os planos (incluindo PETs) são tratados da mesma forma
    if (planData && planData.tiers && planData.tiers.length > 0) {
      pricingTiers = planData.tiers;
      minValue = planData.minValue;
      
      // Formatar o preço
      if (minValue > 0) {
        priceRange = `A partir de ${formatters.formatCurrency(minValue)}/mês`;
      }
    }

    return {
      ...basePlan,
      priceRange,
      pricingTiers,
      minValue
    };
  }, [pricing]);

  const plansList = useMemo(() => {
    if (!basePlans) return [];
    return Object.values(basePlans)
      .sort((a, b) => (a.ordem || 999) - (b.ordem || 999)) // Ordenar por campo ordem crescente
      .map((p) => buildPlan(p));
  }, [buildPlan, basePlans]);

  const handlePlanClick = (idPlano) => {
    if (!basePlans) return;
    const base = basePlans[idPlano];
    if (!base) return;
    const plan = buildPlan(base);
    onPlanSelect(plan);
  };

  const handleRadioChange = (idPlano) => {
    if (!basePlans) return;
    const base = basePlans[idPlano];
    if (!base) return;
    const plan = buildPlan(base);
    onPlanSelect(plan);
  };

  // Mostrar loading enquanto carrega
  if (loading) {
    return (
      <PlansContainer>
        <StateBox>
          <StateTitle><Loader /> Carregando planos…</StateTitle>
        </StateBox>
      </PlansContainer>
    );
  }

  // Mostrar erro se houver
  if (loadError) {
    return (
      <PlansContainer>
        <StateBox $error>
          <StateTitle>{loadError}</StateTitle>
          <ReloadButton onClick={() => window.location.reload()}>
            Recarregar
          </ReloadButton>
        </StateBox>
      </PlansContainer>
    );
  }

  return (
    <>
      <PlansContainer>
        {plansList.length === 0 ? (
          <StateBox>
            <StateTitle>Nenhum plano disponível</StateTitle>
          </StateBox>
        ) : (
          plansList.map((plan) => (
          <PlanCard
            key={plan.idPlano}
            selected={selectedPlan?.idPlano === plan.idPlano}
            onClick={() => handlePlanClick(plan.idPlano)}
          >
            {plan.badge && (
              <PlanBadge type={plan.badge.type}>
                {plan.badge.text}
              </PlanBadge>
            )}
            
            <PlanHeader>
              <div style={{ position: 'relative' }}>
                <RadioInput
                  type="radio"
                  id={`plan-${plan.idPlano}`}
                  name="selectedPlan"
                  value={plan.idPlano}
                  checked={selectedPlan?.idPlano === plan.idPlano}
                  onChange={() => handleRadioChange(plan.idPlano)}
                  onClick={(e) => e.stopPropagation()}
                />
                <SelectionCircle $selected={selectedPlan?.idPlano === plan.idPlano}>
                  <LuCheck size={13} strokeWidth={3} />
                </SelectionCircle>
              </div>
              <PlanInfo>
                <PlanTitle htmlFor={`plan-${plan.idPlano}`}>
                  {plan.name}
                </PlanTitle>
                <PlanPriceRange>{plan.priceRange}</PlanPriceRange>
              </PlanInfo>
            </PlanHeader>
            
            {isMobile ? (
              <>
                <PlanDetailsToggle
                  type="button"
                  onClick={(e) => togglePlanDetails(plan.idPlano, e)}
                >
                  {expandedPlans.has(plan.idPlano) ? 'Ocultar detalhes' : 'Ver detalhes'}
                  <ExpandIcon $expanded={expandedPlans.has(plan.idPlano)} size={16} />
                </PlanDetailsToggle>
                <CollapsibleDetails $expanded={expandedPlans.has(plan.idPlano)}>
                  <PlanDescription>{plan.description}</PlanDescription>
                  <PlanFeatures>
                    {plan.features.map((feature, index) => (
                      <li key={index}><LuCheck size={14} />{feature}</li>
                    ))}
                  </PlanFeatures>
                </CollapsibleDetails>
              </>
            ) : (
              <>
                <PlanDescription>{plan.description}</PlanDescription>
                <PlanFeatures>
                  {plan.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </PlanFeatures>
              </>
            )}
          </PlanCard>
          ))
        )}
      </PlansContainer>
      
      <ErrorMessage show={error}>
        Por favor, selecione um plano
      </ErrorMessage>
    </>
  );
};

export default PlanSelector;
